import { captureSentryError } from "@/shared/utils/sentry-helpers";
import { MutationCache, QueryCache } from "@tanstack/react-query";
import axios from "axios";

/**
 * Sentry hooks for TanStack Query.
 *
 * Attach the returned caches to your QueryClient so every failed query and
 * mutation is reported to Sentry with meaningful tags:
 *
 *   tags: {
 *     source: "tanstack-query",
 *     kind: "query" | "mutation",
 *     queryKey | mutationKey: <serialized first segment>,
 *     url, method, status
 *   }
 *
 * Noise filters (not reported, matching the RTK Query middleware):
 *   - 401, 403 (auth — UI handles)
 *   - axios cancel / AbortError
 *   - Network offline (no response object)
 *
 * fingerprint groups all failures on the same route+status into one issue.
 */

type HttpDetails = {
  status: string;
  method: string;
  url?: string;
  data?: unknown;
};

const NOISY_STATUSES = new Set([401, 403]);

// Extract status/method/url from an axios error without tight coupling to
// axios's types — falls back cleanly for non-axios thrown values.
const toHttpDetails = (error: unknown): HttpDetails => {
  if (axios.isAxiosError(error)) {
    return {
      status:
        error.response?.status != null ? String(error.response.status) : "0",
      method: error.config?.method?.toUpperCase() ?? "unknown",
      url: error.config?.url,
      data: error.response?.data,
    };
  }
  // fetch() or manually-thrown errors: extract what we can.
  const maybeStatus = (error as { status?: number | string } | null)?.status;
  return {
    status: maybeStatus != null ? String(maybeStatus) : "unknown",
    method: "unknown",
  };
};

const isNoisy = (error: unknown, details: HttpDetails): boolean => {
  const statusNum = Number(details.status);
  if (!Number.isNaN(statusNum) && NOISY_STATUSES.has(statusNum)) return true;
  if (axios.isCancel(error)) return true;
  if (axios.isAxiosError(error)) {
    // No response = network offline / DNS / CORS reject. Not actionable server-side.
    if (!error.response) return true;
    // ECONNABORTED includes both timeouts and aborts.
    if (error.code === "ECONNABORTED") return true;
    if (error.code === "ERR_CANCELED") return true;
  }
  if (error instanceof DOMException && error.name === "AbortError") return true;
  return false;
};

const serializeKey = (key: unknown): string => {
  if (!Array.isArray(key) || key.length === 0) return "unknown";
  const head = key[0];
  if (typeof head === "string") return head;
  try {
    return JSON.stringify(head).slice(0, 100);
  } catch {
    return "unserializable";
  }
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export const createSentryQueryCache = (): QueryCache =>
  new QueryCache({
    onError: (error, query) => {
      const details = toHttpDetails(error);
      if (isNoisy(error, details)) return;

      const queryKey = serializeKey(query.queryKey);
      captureSentryError(toError(error), {
        tags: {
          source: "tanstack-query",
          kind: "query",
          queryKey,
          method: details.method,
          status: details.status,
        },
        extra: {
          url: details.url,
          fullQueryKey: query.queryKey,
          data: details.data,
        },
        fingerprint: ["tanstack-query", "query", queryKey, details.status],
        level: "error",
      });
    },
  });

type MutationCacheConfig = NonNullable<
  ConstructorParameters<typeof MutationCache>[0]
>;

export const createSentryMutationCache = (
  extra: Pick<MutationCacheConfig, "onSuccess" | "onSettled"> = {},
): MutationCache =>
  new MutationCache({
    ...extra,
    onError: (error, variables, _onMutateResult, mutation) => {
      const details = toHttpDetails(error);
      if (isNoisy(error, details)) return;

      const mutationKey = serializeKey(mutation.options.mutationKey);
      captureSentryError(toError(error), {
        tags: {
          source: "tanstack-query",
          kind: "mutation",
          mutationKey,
          method: details.method,
          status: details.status,
        },
        extra: {
          url: details.url,
          variables,
          data: details.data,
        },
        fingerprint: [
          "tanstack-query",
          "mutation",
          mutationKey,
          details.status,
        ],
        level: "error",
      });
    },
  });
